// ======================================================================
// ImsPrintPdfButton.js
// Componente per la stampa in PDF della tabella report vendite.
//
// ARCHITETTURA:
// Questo componente genera una pagina HTML stampabile come STRINGA,
// la inietta in un iframe nascosto e avvia il dialog di stampa del
// browser. Non usa window.open() (che su Android viene bloccato dal
// popup blocker) né react-to-print (che usa ReactDOM.renderToString
// non funzionante nel browser client-side).
//
// FLUSSO LOGICO:
// 1. L'utente clicca il pulsante "Stampa in PDF"
// 2. Il componente crea una stringa HTML con la tabella formattata
// 3. L'HTML viene scritto in un iframe nascosto (#imsPrintIframe)
// 4. L'iframe chiama window.print() automaticamente
// 5. Il browser mostra il dialog di stampa con SOLO la tabella
// 6. Dopo la stampa, l'iframe viene rimosso dal DOM
// ======================================================================

import React, { useMemo, useState, useCallback, useRef } from "react";
import { Button } from "react-bootstrap";
import { Tooltip } from "@material-ui/core";
import { PictureAsPdf } from "@material-ui/icons";
// Utility per calcolare i totali per periodo e formattare le etichette
import { calculatePeriodTotals, formatPeriodLabel, sortPeriodsChronologically } from "./ImsPeriodUtils";
// Icone trend (importate ma ora usiamo testo ▲ ▼ — nell'HTML stampato)
import { TrendIcon } from "./ImsTrendIcons";

import * as apiProdotti from "../../api/prodotti";

// ======================================================================
// UTILITY: Formattazione data/ora in italiano
// ======================================================================
function formatDateTimeIt(date) {
  try {
    return new Intl.DateTimeFormat("it-IT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch (e) {
    return date.toISOString();
  }
}

// ======================================================================
// UTILITY: Rimozione iframe di stampa dal DOM
// Viene chiamato dopo la stampa o in caso di errore per pulire risorse
// ======================================================================
function removePrintIframe() {
  try {
    const el = document.getElementById("imsPrintIframe");
    if (el && el.parentNode) el.parentNode.removeChild(el);
  } catch (e) {}
}

// ======================================================================
// UTILITY: Estrazione del valore di una cella dalla riga
//
// Gli oggetti riga (row) hanno una struttura particolare:
// - row.periodi: oggetto { "2024-01": 10, "2024-02": 15 }
// - row.fatturato: oggetto { "2024-01": 100.50, "2024-02": 150.75 }
// - Le colonne nella tabella hanno nomi come "qty_2024-01" o "fatturato_2024-01"
//
// Questa funzione mappa i nomi delle colonne ai valori corretti:
// - Se la colonna inizia con "qty_" → legge da row.periodi
// - Se la colonna inizia con "fatturato_" → legge da row.fatturato
// - Altrimenti → legge direttamente row[colName]
// ======================================================================
function resolveCellValue(row, colName) {
  if (!row) return "";
  // Colonne quantità: estrae il periodo dal nome (es. "qty_2024-01" → "2024-01")
  if (colName && typeof colName === "string" && colName.startsWith("qty_")) {
    const period = colName.replace("qty_", "");
    return row.periodi && Object.prototype.hasOwnProperty.call(row.periodi, period) ? row.periodi[period] : 0;
  }
  // Colonne fatturato: estrae il periodo dal nome
  if (colName && typeof colName === "string" && colName.startsWith("fatturato_")) {
    const period = colName.replace("fatturato_", "");
    return row.fatturato && Object.prototype.hasOwnProperty.call(row.fatturato, period) ? row.fatturato[period] : 0;
  }
  // Colonne normali (productName, trend, diff, etc.)
  return Object.prototype.hasOwnProperty.call(row, colName) ? row[colName] : "";
}

// ======================================================================
// UTILITY: Normalizzazione e filtraggio delle colonne
//
// Input: array di colonne con proprietà { name, title, ... }
// Output: array pulito con solo { name, title }, filtrando:
// - Colonne non valide (senza name o title)
// - Colonne nascoste (nella lista hiddenColumnNames)
// ======================================================================
function normalizeColumns(columns, hiddenColumnNames) {
  if (!Array.isArray(columns)) return [];
  const hiddenSet = new Set(Array.isArray(hiddenColumnNames) ? hiddenColumnNames : []);
  return columns
    .filter((c) => c && typeof c.name === "string" && typeof c.title === "string") // Valide
    .filter((c) => !hiddenSet.has(c.name)) // Non nascoste
    .map((c) => ({ name: c.name, title: c.title })); // Solo name + title
}

// ======================================================================
// UTILITY: Ordinamento righe per nome prodotto (alfabetico)
// Usato per avere la tabella stampata in ordine coerente
// ======================================================================
function sortRowsByProductName(rows) {
  const safe = Array.isArray(rows) ? rows.slice(0) : [];
  return safe.sort((a, b) => {
    const aName = String(a?.productName ?? "").toLowerCase();
    const bName = String(b?.productName ?? "").toLowerCase();
    return aName.localeCompare(bName);
  });
}

// ======================================================================
// CONFIGURAZIONE: Colore trend per fatturato
// Se true, il colore del fatturato segue il trend (verde=positivo, rosso=negativo)
// ======================================================================
const useTrendColorForFatturato = true;

// ======================================================================
// UTILITY: Calcolo colore per colonna fatturato
//
// Confronta il fatturato del periodo corrente con quello del periodo
// precedente per determinare se il trend è positivo (verde) o negativo (rosso).
//
// Parametri:
// - row: la riga completa con tutti i dati
// - value: il valore del fatturato corrente
// - period: il periodo corrente (es. "2024-01")
// - useTrend: se true, usa il trend della riga invece del confronto
//
// Ritorna: { color: "green"/"red"/undefined, fontWeight: 400/700 }
// ======================================================================
function getFatturatoColor(row, value, period, useTrend) {
  // Se attivo il colore basato sul trend della riga
  if (useTrend) {
    if (value == null || isNaN(value)) return { color: undefined, fontWeight: 400 };
    return {
      color: row?.trend === "negative" ? "red" : "green",
      fontWeight: 700,
    };
  }

  // Altrimenti confronta con il periodo precedente
  const allPeriods = sortPeriodsChronologically(Object.keys(row.fatturato || {}));
  const isAggregated = period.includes(" - ");
  // Filtra i periodi dello stesso tipo (aggregati o singoli)
  const relevantPeriods = allPeriods.filter((p) => (isAggregated ? p.includes(" - ") : !p.includes(" - ")));
  const index = relevantPeriods.indexOf(period);
  const prevPeriod = index > 0 ? relevantPeriods[index - 1] : null;
  const prevValue = prevPeriod != null ? row.fatturato?.[prevPeriod] : null;
  // Verde se il valore attuale è maggiore del precedente, rosso altrimenti
  const color =
    prevValue != null && !isNaN(prevValue) ? (Number(prevValue) < Number(value) ? "green" : "red") : undefined;
  const fontWeight = prevValue != null && !isNaN(prevValue) ? 700 : 400;

  return { color, fontWeight };
}

// ======================================================================
// UTILITY: Escape HTML per prevenire XSS
//
// Quando generiamo HTML come stringa, i caratteri speciali (& < > " ')
// devono essere convertiti in entità HTML per evitare iniezioni.
// ======================================================================
function escapeHtml(str) {
  if (str == null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// ======================================================================
// UTILITY: Renderizza icona trend come testo
//
// Invece di usare il componente React <TrendIcon>, usiamo caratteri
// Unicode (▲ ▼ —) perché l'HTML stampato è una stringa, non un
// componente React.
// ======================================================================
function renderTrendIcon(trend) {
  if (trend === "positive") return "▲";
  if (trend === "negative") return "▼";
  return "—";
}

// ======================================================================
// UTILITY PRINCIPALE: Genera l'HTML della tabella stampata
//
// Questo è il cuore del componente. Riceve le colonne e le righe già
// normalizzate e produce una stringa HTML completa con:
// - Se aggregationType + groupedData sono presenti: genera sezioni
//   separate per ogni gruppo (per regione, provincia, linea)
// - Altrimenti: genera una tabella piatta con tutte le righe
//
// Parametri:
// - safeColumns: colonne normalizzate (solo name + title)
// - safeRows: righe ordinate per nome prodotto (null se groupedData)
// - aggregationType: tipo di aggregazione ("region", "province", "line", "global")
// - groupedData: dati raggruppati per gruppo
// ======================================================================
function renderPrintTable(safeColumns, safeRows, aggregationType, groupedData) {
  // Se non c'è aggregazione, usiamo le righe normali
  const rowsToRender = aggregationType && groupedData
    ? null
    : safeRows;

  // CASO 1: Dati raggruppati (per regione/provincia/linea)
  if (aggregationType && groupedData) {
    const sections = [];
    // Per ogni gruppo (es. "Lombardia", "Veneto", ...)
    for (const [groupName, groupRows] of Object.entries(groupedData)) {
      const safeGroupRows = sortRowsByProductName(groupRows);
      // Calcola i totali per periodo per questo gruppo
      const totals = calculatePeriodTotals(safeGroupRows);

      // Genera HTML per i totali del gruppo
      let totalsHtml = "";
      for (let i = 0; i < totals.length; i++) {
        const { periodo, total, trend, fatturatoTotal } = totals[i];
        const fattValue =
          fatturatoTotal == null || isNaN(fatturatoTotal)
            ? ""
            : `€${Number(fatturatoTotal).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const trendSymbol = renderTrendIcon(trend);
        const color = trend === "positive" ? "green" : trend === "negative" ? "red" : "inherit";
        totalsHtml += `<div style="font-size:13px;color:${color};margin-bottom:2px;">
          <span>${formatPeriodLabel(periodo)}: </span>
          <span style="font-weight:700;">${Number(total).toLocaleString("it-IT")} pz.</span>
          ${fattValue ? `<span> ${fattValue} ${trendSymbol}</span>` : ""}
        </div>`;
      }

      // Genera HTML della tabella per questo gruppo
      let tableHtml = buildTableHtml(safeColumns, safeGroupRows);

      // Aggiungi la sezione completa (nome + totali + tabella)
      sections.push(`
        <div style="margin-bottom:20px;">
          <div style="font-size:22px;font-weight:bold;margin-bottom:5px;">${escapeHtml(groupName)}</div>
          <div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:6px;">${totalsHtml}</div>
          ${tableHtml}
        </div>
      `);
    }
    return sections.join("");
  }

  // CASO 2: Tabella piatta (tutte le righe in un'unica tabella)
  return buildTableHtml(safeColumns, rowsToRender);
}

// ======================================================================
// UTILITY PRINCIPALE: Genera HTML di una singola tabella
//
// Costruisce il markup HTML della tabella riga per riga, colonna per
// colonna, applicando stili specifici in base al tipo di colonna:
// - productName: allineato a sinistra
// - trend: carattere Unicode (▲ ▼ —)
// - diff/percentage: colore verde/rosso con grassetto se positivo
// - fatturato_X: formattato come valuta con colore basato sul trend
// - altre colonne: allineamento centrato
//
// Parametri:
// - safeColumns: array di colonne normalizzate
// - safeRows: array di righe ordinate
// ======================================================================
function buildTableHtml(safeColumns, safeRows) {
  // ---- GENERA HEADER DELLA TABELLA ----
  let theadHtml = "<tr>";
  for (const c of safeColumns) {
    theadHtml += `<th>${escapeHtml(c.title.toUpperCase())}</th>`;
  }
  theadHtml += "</tr>";

  // ---- GENERA CORPO DELLA TABELLA ----
  let tbodyHtml = "";
  for (let i = 0; i < safeRows.length; i++) {
    const row = safeRows[i];
    tbodyHtml += "<tr>";
    for (const c of safeColumns) {
      // Estrai il valore della cella dalla riga
      const value = resolveCellValue(row, c.name);
      // Stile base per tutte le celle
      let cellStyle = "text-align:center;font-size:10px;padding:4px 6px;";

      // ---- COLONNA: productName ----
      if (c.name === "productName") {
        cellStyle += "text-align:left;font-size:10px;";
        tbodyHtml += `<td style="${cellStyle}">${escapeHtml(value)}</td>`;
        continue;
      }

      // ---- COLONNA: linea ----
      if (c.name === "linea") {
        cellStyle += "text-align:center;font-size:10px;";
        tbodyHtml += `<td style="${cellStyle}">${escapeHtml(value)}</td>`;
        continue;
      }

      // ---- COLONNA: trend ----
      if (c.name === "trend") {
        const symbol = renderTrendIcon(value);
        tbodyHtml += `<td style="${cellStyle}">${symbol}</td>`;
        continue;
      }

      // ---- COLONNA: diff (differenza media) ----
      if (c.name === "diff") {
        const color = value > 0 ? "green" : "red";
        const fw = value > 0 ? 700 : 400;
        cellStyle += `color:${color};font-weight:${fw};`;
        tbodyHtml += `<td style="${cellStyle}">${value}</td>`;
        continue;
      }

      // ---- COLONNA: percentage (variazione %) ----
      if (c.name === "percentage") {
        const color = value > 0 ? "green" : "red";
        const fw = value > 0 ? 700 : 400;
        cellStyle += `color:${color};font-weight:${fw};`;
        tbodyHtml += `<td style="${cellStyle}">${value}</td>`;
        continue;
      }

      // ---- COLONNE: fatturato_X (fatturato per periodo) ----
      if (c.name.startsWith("fatturato_")) {
        const period = c.name.replace("fatturato_", "");
        // Calcola il colore in base al confronto con il periodo precedente
        const { color, fontWeight } = getFatturatoColor(row, value, period, useTrendColorForFatturato);
        const formattedValue =
          value == null
            ? ""
            : `€${Number(value).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        cellStyle += `color:${color || "inherit"};font-weight:${fontWeight};`;
        tbodyHtml += `<td style="${cellStyle}">${formattedValue}</td>`;
        continue;
      }

      // ---- COLONNE GENERICHE ----
      const printable = value == null ? "" : String(value);
      tbodyHtml += `<td style="${cellStyle}">${escapeHtml(printable)}</td>`;
    }
    tbodyHtml += "</tr>";
  }

  // ---- RESTITUISCE LA TABELLA COMPLETA ----
  // Combina header e corpo in un unico elemento <table>
  // Le style inline sull'header vengono applicate con replace
  return `
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>${theadHtml.replace(/<th/g, '<th style="background:#f3f4f6;font-weight:700;font-size:10px;border:1px solid #e5e7eb;padding:4px 6px;"')}</tr></thead>
      <tbody>${tbodyHtml}</tbody>
    </table>
  `;
}

// ======================================================================
// COMPONENTE PRINCIPALE: ImsPrintPdfButton
//
// Props in ingresso:
// - rows: array di dati grezzi da stampare
// - columns: array di definizioni colonne { name, title }
// - title: titolo del report
// - subtitle: sottotitolo (data/ora generazione)
// - disabled: se il pulsante è disabilitato
// - className: classi CSS aggiuntive
// - onError: callback di errore
// - aggregationType: tipo di aggregazione ("region", "province", "line", "global")
// - groupedData: dati già raggruppati per aggregazione
// - hiddenColumnNames: array di nomi di colonne da nascondere
//
// COME FUNZIONA:
// 1. Quando l'utente clicca il pulsante, doPrint() viene chiamato
// 2. doPrint() genera l'HTML della tabella come stringa
// 3. L'HTML viene scritto in un iframe nascosto (#imsPrintIframe)
// 4. L'iframe chiama automaticamente window.print()
// 5. Il browser mostra il dialog di stampa con SOLO la tabella
// 6. Dopo 10 secondi, l'iframe viene rimosso dal DOM
//
// PERCHE' USA UN IFRAME E NON window.open():
// - Su Android Chrome, window.open() viene spesso bloccato dal popup blocker
// - L'iframe è creato come parte del click handler dell'utente, quindi
//   non viene mai bloccato
// - window.print() chiamato dall'iframe funziona su tutti i browser
// ======================================================================
function ImsPrintPdfButton({
  rows,
  columns,
  title,
  subtitle,
  disabled,
  className,
  onError,
  aggregationType,
  groupedData,
  hiddenColumnNames,
}) {
  // Stato per disabilitare il pulsante durante la stampa
  const [isPrinting, setIsPrinting] = useState(false);
  // Timeout di sicurezza: resetta lo stato dopo 15 secondi in caso di errori
  const unlockTimeoutRef = useRef(null);

  // Titolo e sottotitolo con fallback
  const safeTitle = useMemo(() => title || "Report IMS", [title]);
  const safeSubtitle = useMemo(() => subtitle || `Generato il ${formatDateTimeIt(new Date())}`, [subtitle]);

  // Se c'è un'aggregazione, il pulsante occupa tutta la larghezza
  const isFullWidth = aggregationType != null && aggregationType !== "null";
  const buttonClassName = `${className || ""}${isFullWidth ? " w-100" : ""}`.trim();

  // ======================================================================
  // CSS IN-Line per la pagina di stampa
  //
  // Questo CSS viene iniettato nell'iframe e controlla l'aspetto della
  // tabella stampata. Include:
  // - @page: formato A4 orizzontale con margini di 12mm
  // - Stili per header, tabelle, celle
  // - @media print: nasconde tutto tranne la tabella (fallback)
  // ======================================================================
  const pageStyle = useMemo(() => {
    return `
      @page { size: A4 landscape; margin: 12mm; }
      html, body { height: 100%; margin: 0; padding: 0; }
      body { font-family: Arial, Helvetica, sans-serif; color: #111827; }
      * { box-sizing: border-box; }
      .ims-print-root { padding: 12mm; }
      .ims-print-header { margin-bottom: 10px; }
      .ims-print-title { font-size: 16px; font-weight: 700; margin: 0 0 2px 0; }
      .ims-print-subtitle { font-size: 12px; color: #4b5563; margin: 0; }
      .ims-print-table { width: 100%; border-collapse: collapse; }
      .ims-print-table thead { display: table-header-group; }
      .ims-print-table tr { page-break-inside: avoid; break-inside: avoid; }
      .ims-print-table th, .ims-print-table td { border: 1px solid #e5e7eb; padding: 4px 6px; vertical-align: top; }
      .ims-print-table th { background: #f3f4f6; font-weight: 700; font-size: 10px; }
      .ims-print-table td { font-size: 10px; }
      /* Fallback: se la stampa non usa l'iframe, nasconde tutto tranne la tabella */
      @media print {
        body * { visibility: hidden; }
        .ims-print-root, .ims-print-root * { visibility: visible; }
        .ims-print-root { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
      }
    `;
  }, []);

  // ======================================================================
  // FUNZIONE PRINCIPALE: doPrint
  //
  // Questo è il cuore del componente. Viene chiamato quando l'utente
  // clicca il pulsante "Stampa in PDF".
  //
  // APPROCCIO UNIVERSALE:
  // Usa window.open() chiamato SINCRONAMENTE da click handler - questo è
  // l'unico metodo che funziona su TUTTI i browser includendo mobile.
  // Il popup blocker blocca solo finestre asincrone, non quelle da click.
  //
  // PASSO PASSO:
  // 1. Imposta isPrinting = true per disabilitare il pulsante
  // 2. Imposta un timeout di sicurezza (15 secondi)
  // 3. Normalizza le colonne (filtra quelle nascoste)
  // 4. Ordina le righe per nome prodotto
  // 5. Genera l'HTML della tabella come stringa
  // 6. Apre una nuova finestra con window.open() (synced da click)
  // 7. Scrive l'HTML completo nella finestra
  // 8. La finestra chiama automaticamente window.print()
  // ======================================================================
  const doPrint = useCallback(() => {
    // Disabilita il pulsante per evitare click multipli
    setIsPrinting(true);
    // Resetta timeout precedente se esiste
    if (unlockTimeoutRef.current) window.clearTimeout(unlockTimeoutRef.current);
    // Timeout di sicurezza: resetta lo stato dopo 15 secondi
    unlockTimeoutRef.current = window.setTimeout(() => {
      unlockTimeoutRef.current = null;
      setIsPrinting(false);
    }, 15000);

    try {
      // Rimuove iframe residuo da stampe precedenti
      removePrintIframe();

      // Normalizza le colonne (filtra quelle nascoste)
      const safeColumns = normalizeColumns(columns, hiddenColumnNames);
      // Ordina le righe per nome prodotto (null se ci sono dati raggruppati)
      const safeRows = aggregationType && groupedData ? null : sortRowsByProductName(rows);

      // Genera l'HTML della tabella come STRINGA (non come componente React)
      const printContentHtml = renderPrintTable(safeColumns, safeRows, aggregationType, groupedData);

      // Costruisce il documento HTML completo
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width,initial-scale=1">
          <title>${escapeHtml(safeTitle)}</title>
          <style>${pageStyle}</style>
        </head>
        <body>
          <div class="ims-print-root">
            <div class="ims-print-header">
              <div class="ims-print-title">${escapeHtml(safeTitle)}</div>
              <div class="ims-print-subtitle">${escapeHtml(safeSubtitle)}</div>
            </div>
            ${printContentHtml}
          </div>
          <script>
            document.addEventListener('DOMContentLoaded', function() {
              window.focus();
              setTimeout(function() { window.print(); }, 200);
            });
          </script>
        </body>
        </html>
      `;

      // ======================================================================
      // CREAZIONE FINESTRA DI STAMPA CON window.open()
      //
      // SOLO metodo universale che funziona su:
      // - Desktop (Chrome, Firefox, Safari, Edge)
      // - Mobile (Android Chrome, iOS Safari)
      // - Tablet (iPad)
      //
      // Chiamato SINCRONAMENTE da click handler - il popup blocker
      // non blocca finestre aperte da eventi utente.
      // ======================================================================
      const printWindow = window.open("", "_blank", "width=800,height=600");

      // Verifica se la finestra è stata bloccata
      if (!printWindow || printWindow.closed) {
        throw new Error("Impossibile aprire la finestra di stampa. Verifica che i popup siano consentiti.");
      }

      // Scrive l'HTML nella finestra
      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Timeout di sicurezza: resetta lo stato dopo 15 secondi
      // (la stampa è gestita dal browser nella nuova finestra)
      if (unlockTimeoutRef.current) window.clearTimeout(unlockTimeoutRef.current);
      unlockTimeoutRef.current = window.setTimeout(() => {
        unlockTimeoutRef.current = null;
        setIsPrinting(false);
      }, 15000);
    } catch (e) {
      // In caso di errore, resetta lo stato e chiama il callback di errore
      if (unlockTimeoutRef.current) window.clearTimeout(unlockTimeoutRef.current);
      unlockTimeoutRef.current = null;
      setIsPrinting(false);
      onError(e);
    }
  }, [columns, rows, title, subtitle, aggregationType, groupedData, hiddenColumnNames, pageStyle, safeTitle, safeSubtitle, onError]);

  // ======================================================================
  // RENDER DEL PULSANTE
  //
  // Il componente renderizza solo un pulsante. Non c'è alcun elemento
  // React nascosto nel DOM (come avveniva con react-to-print).
  // Tutto l'HTML viene generato dinamicamente all'interno dell'iframe.
  // ======================================================================
  return (
    <Tooltip title="Stampa in PDF">
      <span style={isFullWidth ? { display: "block", width: "100%" } : undefined}>
        <Button
          variant="outline-danger"
          size="lg"
          onClick={doPrint} // Avvia il flusso di stampa
          disabled={Boolean(disabled) || isPrinting}
          className={buttonClassName}
        >
          <PictureAsPdf className="me-2 mr-2" style={{ fontSize: 18 }} />
          {isPrinting ? "Preparazione..." : "Stampa in PDF"}
        </Button>
      </span>
    </Tooltip>
  );
}

export default ImsPrintPdfButton;
